import { cn } from "@/lib/utils";
import { formatKrCompact } from "@/lib/format";
import type { CustomerListCounterFilter, CustomerListCounters as Counters } from "@/lib/customer-list-calc";

interface CustomerListCountersProps {
  counters: Counters;
  active: CustomerListCounterFilter | null;
  onToggle: (filter: CustomerListCounterFilter) => void;
}

interface TileProps {
  filter: CustomerListCounterFilter;
  label: string;
  value: string;
  sub: string;
  active: boolean;
  onToggle: (filter: CustomerListCounterFilter) => void;
}

function Tile({ filter, label, value, sub, active, onToggle }: TileProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onToggle(filter)}
      className={cn(
        "kpi-card flex min-w-0 flex-col gap-1.5 !p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        active && "accent hover:bg-primary",
      )}
    >
      <span className="label">{label}</span>
      <span className="font-display text-[26px] font-semibold leading-none tracking-tight tabular-nums whitespace-nowrap">{value}</span>
      <span className={cn("text-xs", active ? "opacity-85" : "text-muted-foreground")}>{sub}</span>
    </button>
  );
}

/**
 * Tellerne øverst i kundeliste v2: svarer på «hvem må jeg gjøre noe med nå»
 * før en eneste rad er lest. Hver teller er også et filter (klikk igjen for å
 * nullstille). Tallene regnes på valgt omfang, uavhengig av de andre filtrene.
 */
export default function CustomerListCounters({ counters, active, onToggle }: CustomerListCountersProps) {
  return (
    <section aria-label="Tellere" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile filter="overdue" label="Forfalt" value={String(counters.overdue)} sub="neste steg passert" active={active === "overdue"} onToggle={onToggle} />
      <Tile filter="this_week" label="Denne uken" value={String(counters.thisWeek)} sub="oppfølginger med forfall" active={active === "this_week"} onToggle={onToggle} />
      <Tile filter="cooling" label="Kjølner innen 30 d" value={String(counters.cooling)} sub="varm → medium uten nytt kjøp" active={active === "cooling"} onToggle={onToggle} />
      <Tile
        filter="pipe"
        label="GEEK i pipe"
        value={formatKrCompact(counters.pipeKr)}
        sub={counters.pipeOfferCount === 0
          ? "ingen tilbud ute"
          : `vektet, ${counters.pipeOfferCount} tilbud hos ${counters.pipeCustomerCount} ${counters.pipeCustomerCount === 1 ? "kunde" : "kunder"}`}
        active={active === "pipe"}
        onToggle={onToggle}
      />
    </section>
  );
}
